import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Initial state
const initialState = {
  // App settings
  app: {
    name: 'Admin Dashboard',
    logo: '/Group 37387.png',
    language: 'en',
    timezone: 'UTC'
  },
  
  // Theme settings
  theme: {
    mode: 'light',
    primaryColor: '#5d8efb',
    secondaryColor: '#00ff00',
    backgroundColor: '#f8f9fb',
    cardBackground: '#fff',
    textColor: '#222',
    textSecondaryColor: '#b0b7c3',
    borderColor: '#ececec',
    searchBarColor: '#f5f6fa',
    tableHeaderColor: '#f8f9fb',
    hoverColor: '#f0f4fa',
    linkColor: '#5d8efb',
    linkHoverColor: '#4a7cfa'
  },
  
  // Display settings
  display: {
    sidebarCollapsed: false,
    showNotifications: true,
    autoRefresh: true,
    refreshInterval: 30000, // 30 seconds
    tableRowsPerPage: 10,
    showSearchBar: true,
    compactMode: false
  },
  
  // Notification settings
  notifications: {
    emailNotifications: false,
    browserNotifications: true,
    soundEnabled: false,
    notificationTypes: {
      newUser: true,
      newReport: true,
      systemAlert: true
    }
  },
  
  // Data settings
  data: {
    autoSave: true,
    dataRetention: 90, // days
    exportFormat: 'csv',
    backupEnabled: true
  },
  
  // API settings
  api: {
    baseUrl: 'https://api.uxhq.com',
    timeout: 10000,
    retryAttempts: 3
  }
};

// Action types
const actionTypes = {
  UPDATE_APP: 'UPDATE_APP',
  UPDATE_THEME: 'UPDATE_THEME',
  UPDATE_DISPLAY: 'UPDATE_DISPLAY',
  UPDATE_NOTIFICATIONS: 'UPDATE_NOTIFICATIONS',
  UPDATE_DATA: 'UPDATE_DATA',
  UPDATE_API: 'UPDATE_API',
  RESET_SETTINGS: 'RESET_SETTINGS',
  LOAD_SETTINGS: 'LOAD_SETTINGS'
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_APP:
      return {
        ...state,
        app: { ...state.app, ...action.payload }
      };
    
    case actionTypes.UPDATE_THEME:
      return {
        ...state,
        theme: { ...state.theme, ...action.payload }
      };
    
    case actionTypes.UPDATE_DISPLAY:
      return {
        ...state,
        display: { ...state.display, ...action.payload }
      };
    
    case actionTypes.UPDATE_NOTIFICATIONS:
      return {
        ...state,
        notifications: { ...state.notifications, ...action.payload }
      };
    
    case actionTypes.UPDATE_DATA:
      return {
        ...state,
        data: { ...state.data, ...action.payload }
      };
    
    case actionTypes.UPDATE_API:
      return {
        ...state,
        api: { ...state.api, ...action.payload }
      };
    
    case actionTypes.RESET_SETTINGS:
      return initialState;
    
    case actionTypes.LOAD_SETTINGS:
      return { ...state, ...action.payload };
    
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        dispatch({ type: actionTypes.LOAD_SETTINGS, payload: parsedSettings });
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    }
  }, []);

  // Save settings to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('appSettings', JSON.stringify(state));
  }, [state]);

  // Action creators
  const actions = {
    updateApp: (appUpdates) => {
      dispatch({ type: actionTypes.UPDATE_APP, payload: appUpdates });
    },
    
    updateTheme: (themeUpdates) => {
      dispatch({ type: actionTypes.UPDATE_THEME, payload: themeUpdates });
    },
    
    updateDisplay: (displayUpdates) => {
      dispatch({ type: actionTypes.UPDATE_DISPLAY, payload: displayUpdates });
    },
    
    updateNotifications: (notificationUpdates) => {
      dispatch({ type: actionTypes.UPDATE_NOTIFICATIONS, payload: notificationUpdates });
    },
    
    updateData: (dataUpdates) => {
      dispatch({ type: actionTypes.UPDATE_DATA, payload: dataUpdates });
    },
    
    updateApi: (apiUpdates) => {
      dispatch({ type: actionTypes.UPDATE_API, payload: apiUpdates });
    },
    
    resetSettings: () => {
      dispatch({ type: actionTypes.RESET_SETTINGS });
    }
  };

  const value = {
    state,
    actions
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext; 
